import { fetchBill, fetchBills, updateBills } from 'admin/actions/bills';
import { fetchShopAccounts } from 'admin/actions/shops';
import Headings from 'admin/components/atoms/Headings';
import Input from 'admin/components/atoms/Input';
import Label from 'admin/components/atoms/Label';
import Labeled from 'admin/components/atoms/Labeled';
import TextArea from 'admin/components/atoms/TextArea';
import TextBox from 'admin/components/atoms/TextBox';
import { Account } from 'admin/reducers/shop';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { toQueryParams } from '~/javascripts/admin/utils';
import { Button } from '../../atoms/Buttons';
import { createColorCodedOptions } from '../../atoms/ColorCodedOption';
import Select from '../../atoms/Select';
import ApiResults from '../../molecules/ApiResults';
import BreadCrumb from '../../molecules/BreadCrumb';
import QrCodeDrawer from '../../molecules/QrCodeDrawer';

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '販促管理', href: '', isCurrentPage: true },
  { value: '支払いQRコード管理', href: '/promotion/bills', isCurrentPage: false },
  { value: '支払いQRコード詳細', href: '', isCurrentPage: true },
];

const FormWrapper = styled.div`
  ${Labeled} {
    display: grid;
    grid-template-columns: 1fr 4fr;
    grid-template-rows: auto;
    justify-items: left;
    padding: 16px 0px;
    align-items: start;
    border-bottom: solid;
    border-bottom-width: 1px;
    border-bottom-color: #e8e8e8;
  }
  ${Labeled} > ${Label} {
    grid-column: 1;
  }
  ${TextBox} {
    width: 500px;
    margin-bottom: 10px;
    &.amountArrayInput {
      width: 200px;
    }
  }
  ${TextArea} {
    width: 500px;
    margin-bottom: 10px;
    resize: both;
  }
`;

const LabelValue = styled.span``;

const StoreDetailWrapper = styled.div``;

const StoreDetail = styled.div``;

const SubmitButtonWrapper = styled.div`
  margin-top: 10px;
`;

type Bill = Awaited<ReturnType<typeof fetchBill>> & {
  created_at: string;
  shop_name: string;
  org_name: string;
};

type PrivateMoney = {
  id: string;
  name: string;
  organization?: { code?: string };
  is_suspended: boolean;
};

const STATUS_OPTIONS = [
  {
    label: '有効',
    value: true,
    bgColor: '#21BF25',
  },
  {
    label: '無効',
    value: false,
    bgColor: '#D0D0D0',
  },
];

const formatOptionLabel = createColorCodedOptions(STATUS_OPTIONS);

const BillsDetail = ({ match }: RouteComponentProps<{ id?: string }>) => {
  const [billDetails, setBillDetails] = useState<Bill>();
  const [availableAdditionalMoneys, setAvailableAdditionalMoneys] = useState<PrivateMoney[]>([]);
  const [additionalPrivateMoneyIds, setAdditionalPrivateMoneyIds] = useState<string[]>([]);
  const [apiMessages, setApiMessages] =
    useState<StyledComponentPropsWithRef<typeof ApiResults>['messages']>(undefined);

  const updateBill = (id: string) => {
    updateBills(id, {
      is_disabled: billDetails?.is_disabled,
      additional_private_money_ids: additionalPrivateMoneyIds,
    })
      .then(() => {
        setApiMessages([
          {
            text: '支払いQRコードの更新に成功しました',
            isError: false,
          },
        ]);
      })
      .catch((err) => {
        setApiMessages([
          {
            text: err.message,
            isError: true,
          },
        ]);
      });
  };

  useEffect(() => {
    if (!match.params.id) return;

    const loadData = async () => {
      try {
        // unfortunately the 2 apis return slightly different data that is both needed for the page, and have different structures, hence the Promise below
        const [bill, billsResponse] = await Promise.all([
          fetchBill(match.params.id),
          fetchBills(toQueryParams({ bill_id: match.params.id }, true)),
        ]);

        const {
          rows: [{ created_at, shop_name, org_name }],
        } = billsResponse;
        setBillDetails({ ...bill, created_at, shop_name, org_name });

        const accounts = (await fetchShopAccounts(bill.user.id, 'per_page=100')) as Account[];
        const availableAccounts = accounts
          .filter((a) => a.private_money?.name !== bill.private_money.name)
          .filter((a) => a.private_money?.organization?.code !== 'pocketchange');
        const available = availableAccounts.map(
          (a) => ({ ...a.private_money, is_suspended: a.is_suspended } as PrivateMoney)
        );
        setAvailableAdditionalMoneys(available);

        const availableIds = new Set(available.map((m) => m.id));
        const savedIds = (bill.additional_private_moneys || []).map((m) => m.id);
        setAdditionalPrivateMoneyIds(savedIds.filter((id) => availableIds.has(id)));
      } catch (err) {
        const error = await Promise.resolve(err);
        setApiMessages([{ text: error.message, isError: true }]);
      }
    };
    loadData();
  }, [match.params.id]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>支払いQRコード詳細</Headings.h5>
      <ApiResults messages={apiMessages} />
      {!!billDetails && (
        <>
          <Labeled labelText="支払いQRコードID">
            <LabelValue>{billDetails?.id}</LabelValue>
          </Labeled>
          <Labeled labelText="発行日時">
            <LabelValue>
              {billDetails?.created_at &&
                moment(billDetails?.created_at).format('YYYY/MM/DD HH:mm:ss')}
            </LabelValue>
          </Labeled>
          <Labeled labelText="発行店舗">
            <StoreDetailWrapper>
              <StoreDetail>{`マネー：${billDetails.private_money.name}`}</StoreDetail>
              <StoreDetail>{`組織　：${billDetails?.org_name ?? ''}`}</StoreDetail>
              <StoreDetail style={{ display: 'flex' }}>
                <div style={{ wordBreak: 'keep-all' }}>店舗　：</div>
                <div style={{ wordBreak: 'break-word' }}>{billDetails?.shop_name ?? ''}</div>
              </StoreDetail>
            </StoreDetailWrapper>
          </Labeled>
          <Labeled labelText="金額">
            <LabelValue>{billDetails?.amount?.toLocaleString()}</LabelValue>
          </Labeled>
          <Labeled labelText="説明文">
            <LabelValue style={{ wordBreak: 'break-word' }}>{billDetails?.description}</LabelValue>
          </Labeled>
          <Labeled labelText="追加対応マネー">
            <div>
              {availableAdditionalMoneys.map((money) => (
                <Label
                  key={money.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    cursor: money.is_suspended ? 'not-allowed' : 'pointer',
                    color: money.is_suspended ? '#737373' : 'inherit',
                  }}
                >
                  <Input
                    type="checkbox"
                    checked={additionalPrivateMoneyIds.includes(money.id)}
                    disabled={money.is_suspended}
                    onChange={(e) => {
                      const { checked } = e.target;
                      setAdditionalPrivateMoneyIds((prev) =>
                        checked ? [...prev, money.id] : prev.filter((id) => id !== money.id)
                      );
                    }}
                  />
                  {money.name}
                </Label>
              ))}
            </div>
          </Labeled>

          <Labeled labelText="状態">
            <Select
              options={STATUS_OPTIONS}
              value={STATUS_OPTIONS.find((x) => x.value !== billDetails.is_disabled)}
              isMulti={false}
              formatOptionLabel={formatOptionLabel}
              onChange={(option) =>
                setBillDetails((prev) =>
                  prev ? { ...prev, is_disabled: !option?.value } : undefined
                )
              }
            />
          </Labeled>
          <Labeled labelText="QRコード">
            <QrCodeDrawer
              uri={billDetails?.token}
              name={billDetails?.shop_name}
              qrSize={128}
              showUrl
              urlAsLabel
              copyable
            />
          </Labeled>
          <SubmitButtonWrapper>
            <Button onClick={() => updateBill(billDetails?.id)}>更新</Button>
          </SubmitButtonWrapper>
        </>
      )}
    </FormWrapper>
  );
};

export default BillsDetail;
